import React, { useState, useEffect, useCallback } from 'react';
import { DelegateService } from '../../../services/delegateService';
import { 
  ClientDelegate, 
  StoreDelegate, 
  DelegateStats,
  DelegateWithStore,
  DelegateFormData
} from '../../../types/delegates';
import '../../../styles/components/DelegateManagement.scss';

interface Props {
  clientId: string;
  stores: Array<{ id: string; store_name: string; is_active: boolean }>;
}

const DelegateManagement: React.FC<Props> = ({ clientId, stores }) => {
  const [clientDelegates, setClientDelegates] = useState<ClientDelegate[]>([]);
  const [storeDelegates, setStoreDelegates] = useState<DelegateWithStore[]>([]);
  const [stats, setStats] = useState<DelegateStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDelegate, setSelectedDelegate] = useState<ClientDelegate | StoreDelegate | null>(null);
  const [activeTab, setActiveTab] = useState<'client' | 'store'>('client');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [inviteForm, setInviteForm] = useState<DelegateFormData>({
    email: '',
    name: '',
    role: 'delegate',
    permissions: {
      dashboard: true,
      settings: false,
      stores: true,
      reports: true,
      chatbot: false
    },
    store_ids: []
  });

  const loadDelegates = useCallback(async () => {
    try {
      setIsLoading(true);
      const [clientDelegatesData, storeDelegatesData, statsData] = await Promise.all([
        DelegateService.getClientDelegates(clientId),
        DelegateService.getStoreDelegates(),
        DelegateService.getDelegateStats(clientId)
      ]);

      setClientDelegates(clientDelegatesData);
      setStoreDelegates(storeDelegatesData);
      setStats(statsData);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadDelegates();
  }, [loadDelegates]);

  const handleInvite = async () => {
    try {
      setError('');
      setSuccess('');

      if (!DelegateService.validateEmail(inviteForm.email)) {
        setError('Please enter a valid email address');
        return;
      }

      if (activeTab === 'client') {
        await DelegateService.inviteClientDelegate(clientId, {
          email: inviteForm.email,
          name: inviteForm.name,
          role: inviteForm.role,
          permissions: inviteForm.permissions
        });
        setSuccess('Client delegate invited successfully! Supabase Auth email sent.');
      } else {
        if (!inviteForm.store_ids || inviteForm.store_ids.length === 0) {
          setError('Please select at least one store');
          return;
        }
        await DelegateService.inviteStoreDelegates(inviteForm.store_ids, {
          email: inviteForm.email,
          name: inviteForm.name,
          role: inviteForm.role,
          permissions: inviteForm.permissions
        });
        setSuccess('Store delegates invited successfully! Supabase Auth email sent.');
      }

      setShowInviteModal(false);
      resetForm();
      loadDelegates();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleToggleStatus = async (delegate: ClientDelegate | StoreDelegate) => {
    try {
      setError('');
      setSuccess('');

      const updates = { is_active: !delegate.is_active };
      
      if ('client_id' in delegate) {
        await DelegateService.updateClientDelegate(delegate.id, updates);
        setClientDelegates(prev => prev.map(d => 
          d.id === delegate.id ? { ...d, ...updates } : d
        ));
      } else {
        await DelegateService.updateStoreDelegate(delegate.id, updates);
        setStoreDelegates(prev => prev.map(d => 
          d.id === delegate.id ? { ...d, ...updates } : d
        ));
      }

      setSuccess(`Delegate ${delegate.is_active ? 'deactivated' : 'activated'} successfully!`);
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleDelete = async (delegate: ClientDelegate | StoreDelegate) => {
    if (!window.confirm('Are you sure you want to delete this delegate?')) return;

    try {
      setError('');
      setSuccess('');

      if ('client_id' in delegate) {
        await DelegateService.deleteClientDelegate(delegate.id);
        setClientDelegates(prev => prev.filter(d => d.id !== delegate.id));
      } else {
        await DelegateService.deleteStoreDelegate(delegate.id);
        setStoreDelegates(prev => prev.filter(d => d.id !== delegate.id));
      }

      setSuccess('Delegate deleted successfully!');
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleEdit = (delegate: ClientDelegate | StoreDelegate) => {
    setSelectedDelegate(delegate);
    setInviteForm({
      email: delegate.email,
      name: delegate.name || '',
      role: delegate.role,
      permissions: delegate.permissions,
      store_ids: 'store_id' in delegate ? [delegate.store_id] : undefined
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!selectedDelegate) return;

    try {
      setError('');
      setSuccess('');

      const updates = {
        name: inviteForm.name,
        role: inviteForm.role,
        permissions: inviteForm.permissions
      };

      if ('client_id' in selectedDelegate) {
        await DelegateService.updateClientDelegate(selectedDelegate.id, updates);
        setClientDelegates(prev => prev.map(d => 
          d.id === selectedDelegate.id ? { ...d, ...updates } : d
        ));
      } else {
        await DelegateService.updateStoreDelegate(selectedDelegate.id, updates);
        setStoreDelegates(prev => prev.map(d => 
          d.id === selectedDelegate.id ? { ...d, ...updates } : d
        ));
      }

      setSuccess('Delegate updated successfully!');
      setShowEditModal(false);
      setSelectedDelegate(null);
      resetForm();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const resetForm = () => {
    setInviteForm({
      email: '',
      name: '',
      role: 'delegate',
      permissions: {
        dashboard: true,
        settings: false,
        stores: true,
        reports: true,
        chatbot: false
      },
      store_ids: []
    });
  };

  const getStatusBadge = (delegate: ClientDelegate | StoreDelegate) => {
    if (!delegate.is_active) {
      return <span className="status-badge inactive">Inactive</span>;
    }
    if (!delegate.accepted_at) {
      return <span className="status-badge pending">Pending</span>;
    }
    return <span className="status-badge active">Active</span>;
  };

  const getRoleBadge = (role: string) => {
    const roleColors = {
      delegate: 'blue',
      manager: 'green',
      viewer: 'gray'
    };
    return <span className={`role-badge ${roleColors[role as keyof typeof roleColors]}`}>{role}</span>;
  };

  if (isLoading) {
    return (
      <div className="delegate-management">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading delegates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="delegate-management">
      {/* Header */}
      <div className="delegate-header">
        <h2>👥 Delegate Access</h2>
        <p>Manage who can access your dashboard and stores</p>
        
        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-number">{stats.total_delegates}</span>
              <span className="stat-label">Total Delegates</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{stats.active_delegates}</span>
              <span className="stat-label">Active</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{stats.pending_invites}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{stats.client_delegates}</span>
              <span className="stat-label">Company-wide</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{stats.store_delegates}</span>
              <span className="stat-label">Store-specific</span>
            </div>
          </div>
        )}
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="success-message">
          <span className="success-icon">✅</span>
          {success}
        </div>
      )}
      
      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="delegate-tabs">
        <button 
          className={`tab-btn ${activeTab === 'client' ? 'active' : ''}`}
          onClick={() => setActiveTab('client')}
        >
          🏢 Company Delegates ({clientDelegates.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'store' ? 'active' : ''}`}
          onClick={() => setActiveTab('store')}
        >
          🏪 Store Delegates ({storeDelegates.length})
        </button>
      </div>

      {/* Action Bar */}
      <div className="action-bar">
        <button 
          onClick={() => {
            resetForm();
            setShowInviteModal(true);
          }}
          className="invite-btn"
        >
          ➕ Invite {activeTab === 'client' ? 'Company' : 'Store'} Delegate
        </button>
      </div>

      {/* Delegates List */}
      <div className="delegates-list">
        {activeTab === 'client' ? (
          clientDelegates.length === 0 ? (
            <div className="empty-state">
              <p>No company delegates yet. Invite someone to get started!</p>
            </div>
          ) : (
            clientDelegates.map(delegate => (
              <div key={delegate.id} className={`delegate-card ${!delegate.is_active ? 'inactive' : ''}`}>
                <div className="delegate-info">
                  <div className="delegate-main">
                    <h3>{delegate.name || delegate.email}</h3>
                    <p className="delegate-email">{delegate.email}</p>
                    <div className="delegate-meta">
                      {getRoleBadge(delegate.role)}
                      {getStatusBadge(delegate)}
                      <span className="delegate-date">
                        Invited: {new Date(delegate.invited_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="delegate-permissions">
                    <h4>Permissions:</h4>
                    <div className="permissions-grid">
                      {Object.entries(delegate.permissions).map(([key, value]) => (
                        <span key={key} className={`permission ${value ? 'granted' : 'denied'}`}>
                          {key}: {value ? '✓' : '✗'}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="delegate-actions">
                  <button 
                    onClick={() => handleEdit(delegate)}
                    className="edit-btn"
                  >
                    ✏️ Edit
                  </button>
                  <button 
                    onClick={() => handleToggleStatus(delegate)}
                    className={`toggle-btn ${delegate.is_active ? 'deactivate' : 'activate'}`}
                  >
                    {delegate.is_active ? '🚫 Deactivate' : '✅ Activate'}
                  </button>
                  <button 
                    onClick={() => handleDelete(delegate)}
                    className="delete-btn"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))
          )
        ) : (
          storeDelegates.length === 0 ? (
            <div className="empty-state">
              <p>No store delegates yet. Invite someone to get started!</p>
            </div>
          ) : (
            storeDelegates.map(delegate => (
              <div key={delegate.id} className={`delegate-card ${!delegate.is_active ? 'inactive' : ''}`}>
                <div className="delegate-info">
                  <div className="delegate-main">
                    <h3>{delegate.name || delegate.email}</h3>
                    <p className="delegate-email">{delegate.email}</p>
                    <p className="delegate-store">Store: {delegate.store.store_name}</p>
                    <div className="delegate-meta">
                      {getRoleBadge(delegate.role)}
                      {getStatusBadge(delegate)}
                      <span className="delegate-date">
                        Invited: {new Date(delegate.invited_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="delegate-permissions">
                    <h4>Permissions:</h4>
                    <div className="permissions-grid">
                      {Object.entries(delegate.permissions).map(([key, value]) => (
                        <span key={key} className={`permission ${value ? 'granted' : 'denied'}`}>
                          {key}: {value ? '✓' : '✗'}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="delegate-actions">
                  <button 
                    onClick={() => handleEdit(delegate)}
                    className="edit-btn"
                  >
                    ✏️ Edit
                  </button>
                  <button 
                    onClick={() => handleToggleStatus(delegate)}
                    className={`toggle-btn ${delegate.is_active ? 'deactivate' : 'activate'}`}
                  >
                    {delegate.is_active ? '🚫 Deactivate' : '✅ Activate'}
                  </button>
                  <button 
                    onClick={() => handleDelete(delegate)}
                    className="delete-btn"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))
          )
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Invite {activeTab === 'client' ? 'Company' : 'Store'} Delegate</h3>
              <button onClick={() => setShowInviteModal(false)} className="close-btn">✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="Enter email address"
                  className="modern-input"
                />
              </div>
              
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  placeholder="Enter full name"
                  className="modern-input"
                />
              </div>

              <div className="form-group">
                <label>Role</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as any })}
                  className="modern-select"
                >
                  <option value="delegate">Delegate</option>
                  <option value="manager">Manager</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>

              {activeTab === 'store' && (
                <div className="form-group">
                  <label>Select Stores *</label>
                  <div className="store-selection">
                    {stores.filter(s => s.is_active).map(store => (
                      <label key={store.id} className="store-checkbox">
                        <input
                          type="checkbox"
                          checked={inviteForm.store_ids?.includes(store.id) || false}
                          onChange={(e) => {
                            const storeIds = inviteForm.store_ids || [];
                            if (e.target.checked) {
                              setInviteForm({ ...inviteForm, store_ids: [...storeIds, store.id] });
                            } else {
                              setInviteForm({ ...inviteForm, store_ids: storeIds.filter(id => id !== store.id) });
                            }
                          }}
                        />
                        {store.store_name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Permissions</label>
                <div className="permissions-form">
                  <label className="permission-checkbox">
                    <input
                      type="checkbox"
                      checked={inviteForm.permissions.dashboard}
                      onChange={(e) => setInviteForm({
                        ...inviteForm,
                        permissions: { ...inviteForm.permissions, dashboard: e.target.checked }
                      })}
                    />
                    Dashboard Access
                  </label>
                  
                  {activeTab === 'client' && (
                    <label className="permission-checkbox">
                      <input
                        type="checkbox"
                        checked={inviteForm.permissions.settings}
                        onChange={(e) => setInviteForm({
                          ...inviteForm,
                          permissions: { ...inviteForm.permissions, settings: e.target.checked }
                        })}
                      />
                      Settings Access
                    </label>
                  )}
                  
                  <label className="permission-checkbox">
                    <input
                      type="checkbox"
                      checked={inviteForm.permissions.stores}
                      onChange={(e) => setInviteForm({
                        ...inviteForm,
                        permissions: { ...inviteForm.permissions, stores: e.target.checked }
                      })}
                    />
                    Store Management
                  </label>
                  
                  <label className="permission-checkbox">
                    <input
                      type="checkbox"
                      checked={inviteForm.permissions.reports}
                      onChange={(e) => setInviteForm({
                        ...inviteForm,
                        permissions: { ...inviteForm.permissions, reports: e.target.checked }
                      })}
                    />
                    Reports Access
                  </label>
                  
                  {activeTab === 'store' && (
                    <label className="permission-checkbox">
                      <input
                        type="checkbox"
                        checked={inviteForm.permissions.chatbot}
                        onChange={(e) => setInviteForm({
                          ...inviteForm,
                          permissions: { ...inviteForm.permissions, chatbot: e.target.checked }
                        })}
                      />
                      Chatbot Management
                    </label>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowInviteModal(false)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleInvite} className="save-btn">
                Send Invite
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedDelegate && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Delegate</h3>
              <button onClick={() => setShowEditModal(false)} className="close-btn">✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  disabled
                  className="modern-input disabled"
                />
              </div>
              
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  placeholder="Enter full name"
                  className="modern-input"
                />
              </div>

              <div className="form-group">
                <label>Role</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as any })}
                  className="modern-select"
                >
                  <option value="delegate">Delegate</option>
                  <option value="manager">Manager</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>

              <div className="form-group">
                <label>Permissions</label>
                <div className="permissions-form">
                  <label className="permission-checkbox">
                    <input
                      type="checkbox"
                      checked={inviteForm.permissions.dashboard}
                      onChange={(e) => setInviteForm({
                        ...inviteForm,
                        permissions: { ...inviteForm.permissions, dashboard: e.target.checked }
                      })}
                    />
                    Dashboard Access
                  </label>
                  
                  {activeTab === 'client' && (
                    <label className="permission-checkbox">
                      <input
                        type="checkbox"
                        checked={inviteForm.permissions.settings}
                        onChange={(e) => setInviteForm({
                          ...inviteForm,
                          permissions: { ...inviteForm.permissions, settings: e.target.checked }
                        })}
                      />
                      Settings Access
                    </label>
                  )}
                  
                  <label className="permission-checkbox">
                    <input
                      type="checkbox"
                      checked={inviteForm.permissions.stores}
                      onChange={(e) => setInviteForm({
                        ...inviteForm,
                        permissions: { ...inviteForm.permissions, stores: e.target.checked }
                      })}
                    />
                    Store Management
                  </label>
                  
                  <label className="permission-checkbox">
                    <input
                      type="checkbox"
                      checked={inviteForm.permissions.reports}
                      onChange={(e) => setInviteForm({
                        ...inviteForm,
                        permissions: { ...inviteForm.permissions, reports: e.target.checked }
                      })}
                    />
                    Reports Access
                  </label>
                  
                  {activeTab === 'store' && (
                    <label className="permission-checkbox">
                      <input
                        type="checkbox"
                        checked={inviteForm.permissions.chatbot}
                        onChange={(e) => setInviteForm({
                          ...inviteForm,
                          permissions: { ...inviteForm.permissions, chatbot: e.target.checked }
                        })}
                      />
                      Chatbot Management
                    </label>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowEditModal(false)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleUpdate} className="save-btn">
                Update Delegate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DelegateManagement; 